import { useEffect, useState } from "react";
import {
  DEFAULT_KILLZONES,
  DEFAULT_SESSION_OVERLAY,
  KILLZONE_COLORS,
  MAX_KILLZONE_NAME,
  SESSION_OPEN_KINDS,
  SESSION_OPEN_NAME,
  parseHhmm,
  type Killzone,
  type KillzoneColor,
  type SessionOverlay,
} from "@helm/core/bars";

/** Chart theme token behind each killzone colour (the band is that colour, faint). */
const COLOR_VAR: Record<KillzoneColor, string> = {
  accent: "var(--accent)",
  up: "var(--buy)",
  down: "var(--sell)",
  last: "var(--last)",
  text: "var(--chart-text)",
  textDim: "var(--text-3)",
};

const COLOR_LABEL: Record<KillzoneColor, string> = {
  accent: "Accent",
  up: "Buy",
  down: "Sell",
  last: "Last",
  text: "Text",
  textDim: "Dim",
};

/** Name field: edited locally, saved on blur / Enter (so spaces can be typed). */
function NameInput({ value, onCommit }: { value: string; onCommit: (s: string) => void }): React.JSX.Element {
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    setDraft(value);
  }, [value]);
  const commit = (): void => {
    const s = draft.trim().slice(0, MAX_KILLZONE_NAME);
    if (s.length > 0 && s !== value) onCommit(s);
    else setDraft(value);
  };
  return (
    <input
      className="kz-name"
      type="text"
      value={draft}
      maxLength={MAX_KILLZONE_NAME}
      aria-label="Killzone name"
      onChange={(e) => {
        setDraft(e.target.value);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
      }}
    />
  );
}

/**
 * Session and time switches for one chart pane (B3), inside the chart's overlay menu: prior day
 * close, the three named opens, and the killzone editor (New York times; rename, retime, recolour
 * and toggle each band). Saved with the pane's overlays (`chartLayout.panes[i].overlays.session`).
 */
export function SessionOverlayMenu({
  value,
  onChange,
}: {
  value: SessionOverlay | undefined;
  onChange: (s: SessionOverlay) => void;
}): React.JSX.Element {
  const s = value ?? DEFAULT_SESSION_OVERLAY;
  const set = (patch: Partial<SessionOverlay>): void => {
    onChange({ ...s, zones: s.zones.map((z) => ({ ...z })), ...patch });
  };
  const setZone = (i: number, patch: Partial<Killzone>): void => {
    const zones = s.zones.map((z, k) => (k === i ? { ...z, ...patch } : { ...z }));
    const z = zones[i];
    // Keep only a valid band (both times set, not equal); the time inputs only emit HH:MM or "".
    if (!z || parseHhmm(z.start) === null || parseHhmm(z.end) === null || z.start === z.end) return;
    onChange({ ...s, zones });
  };
  return (
    <div className="session-menu" role="group" aria-label="Session and time">
      <div className="session-menu-title">Session &amp; time (New York)</div>
      <label
        className="check-row"
        title="Close of the prior CME trading day's last bar, from the chart's bars (not the exchange settlement)"
      >
        <input
          type="checkbox"
          checked={s.pdc}
          onChange={(e) => {
            set({ pdc: e.target.checked });
          }}
        />
        <span>Prior day close (PD-C)</span>
      </label>
      {SESSION_OPEN_KINDS.map((k) => (
        <label key={k} className="check-row" title="Vertical line at the open and its opening price">
          <input
            type="checkbox"
            checked={s[k]}
            onChange={(e) => {
              set({ [k]: e.target.checked });
            }}
          />
          <span>{SESSION_OPEN_NAME[k]}</span>
        </label>
      ))}
      <label className="check-row" title="Time bands in New York time, DST-correct (intraday time charts)">
        <input
          type="checkbox"
          checked={s.killzones}
          onChange={(e) => {
            set({ killzones: e.target.checked });
          }}
        />
        <span>Killzones</span>
      </label>
      {s.killzones && (
        <div className="kz-list">
          {s.zones.map((z, i) => (
            <div key={z.id} className="kz-row">
              <input
                type="checkbox"
                checked={z.on}
                aria-label={`Show ${z.name}`}
                onChange={(e) => {
                  setZone(i, { on: e.target.checked });
                }}
              />
              <NameInput
                value={z.name}
                onCommit={(name) => {
                  setZone(i, { name });
                }}
              />
              <input
                type="time"
                className="kz-time"
                value={z.start}
                aria-label={`${z.name} start (New York)`}
                onChange={(e) => {
                  setZone(i, { start: e.target.value });
                }}
              />
              <span className="kz-dash">–</span>
              <input
                type="time"
                className="kz-time"
                value={z.end}
                aria-label={`${z.name} end (New York)`}
                onChange={(e) => {
                  setZone(i, { end: e.target.value });
                }}
              />
              <select
                className="kz-color"
                value={z.color}
                aria-label={`${z.name} colour`}
                style={{ borderLeftColor: COLOR_VAR[z.color] }}
                onChange={(e) => {
                  setZone(i, { color: e.target.value as KillzoneColor });
                }}
              >
                {KILLZONE_COLORS.map((c) => (
                  <option key={c} value={c}>
                    {COLOR_LABEL[c]}
                  </option>
                ))}
              </select>
            </div>
          ))}
          <button
            type="button"
            className="kz-reset"
            onClick={() => {
              set({ zones: DEFAULT_KILLZONES.map((z) => ({ ...z })) });
            }}
          >
            Reset killzones
          </button>
        </div>
      )}
    </div>
  );
}
